import React, { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import ImageUploadInput from '../components/ImageUploadInput';
import BugActivity from '../components/BugActivity';
import useSortAndFilterBugs from '../hooks/useSortAndFilterBugs';
import {
  validateMediaFiles,
  getMediaErrorMessage,
} from '../utils/imageUpload';
import { normalizeMediaList } from '../utils/media';

export default function BugsPage() {
  const { t, lang } = useLang();
  const [list, setList] = useState([]);
  const [desc, setDesc] = useState('');
  const [details, setDetails] = useState('');
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editDesc, setEditDesc] = useState('');
  const [editDetails, setEditDetails] = useState('');
  const [editFile, setEditFile] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [viewImg, setViewImg] = useState(null);
  const [newComments, setNewComments] = useState({});
  const [showComments, setShowComments] = useState({});
  const [reporting, setReporting] = useState(false);
  const [savingEditId, setSavingEditId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [highlightIds, setHighlightIds] = useState({});
  const [prioritySaving, setPrioritySaving] = useState({});
  const [pinned, setPinned] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('pinnedBugs') || '[]');
    } catch {
      return [];
    }
  });
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const token = localStorage.getItem('token');
  const API_URL = process.env.REACT_APP_API_URL || '';
  const decode = (tok) => {
    if (!tok) return {};
    try {
      const d = JSON.parse(atob(tok.split('.')[1]));
      return { role: d.role, id: d.id };
    } catch {
      return {};
    }
  };
  const { role, id: userId } = decode(token);

  const {
    list: filteredList,
    shown,
    total,
    onlyMine,
    setOnlyMine,
    showClosed,
    setShowClosed,
    onlyUrgent,
    setOnlyUrgent,
    sort,
    setSort,
  } = useSortAndFilterBugs(list, userId);

  const pinnedListView = filteredList.filter((b) => pinned.includes(b._id));
  const regularListView = filteredList.filter((b) => !pinned.includes(b._id));

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const fetchBugs = () => {
    fetch(`${API_URL}/api/bugs?includeClosed=true`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((d) => {
        const arr = Array.isArray(d) ? d : [];
        setList(arr);
        const highlights = {};
        arr.forEach((b) => {
          const lastComment = Array.isArray(b.comments)
            ? b.comments[b.comments.length - 1]
            : null;
          const commentTime = lastComment ? new Date(lastComment.createdAt).getTime() : 0;
          const statusTime = b.statusChangedAt ? new Date(b.statusChangedAt).getTime() : 0;
          const activityTime = b.lastActivityAt
            ? new Date(b.lastActivityAt).getTime()
            : 0;
          const lastChange = Math.max(commentTime, statusTime, activityTime);
          if (Date.now() - lastChange < 10 * 60 * 1000) {
            highlights[b._id] = true;
            setTimeout(() => {
              setHighlightIds((p) => {
                const { [b._id]: _, ...rest } = p;
                return rest;
              });
            }, 5000);
          }
        });
        if (Object.keys(highlights).length) {
          setHighlightIds((p) => ({ ...p, ...highlights }));
        }
      })
      .catch((err) => console.error('Failed to fetch bugs', err));
  };

  useEffect(() => {
    fetchBugs();
  }, []);

  useEffect(() => {
    localStorage.setItem('pinnedBugs', JSON.stringify(pinned));
  }, [pinned]);

  useEffect(() => {
    setPinned((prev) =>
      prev.filter((id) =>
        list.some(
          (b) => b._id === id && (b.status || '').toLowerCase() !== 'closed'
        )
      )
    );
  }, [list]);

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    const err = validateMediaFiles(files, 1);
    if (err) {
      alert(getMediaErrorMessage(err, t));
      e.target.value = '';
      setFile(null);
    } else {
      setFile(files[0]);
    }
  };

  const handleEditFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    const err = validateMediaFiles(files, 1);
    if (err) {
      alert(getMediaErrorMessage(err, t));
      e.target.value = '';
      setEditFile(null);
    } else {
      setEditFile(files[0]);
    }
  };

  const canManagePriority = (bug) =>
    bug?.createdBy?.id === userId || ['admin', 'superuser'].includes(role);

  const changePriority = async (bug, delta) => {
    if (!canManagePriority(bug)) return;
    const current = Number.isFinite(bug?.priority) ? bug.priority : 1;
    const next = Math.min(10, Math.max(1, current + delta));
    if (next === current) return;
    setPrioritySaving((prev) => ({ ...prev, [bug._id]: true }));
    try {
      const res = await fetch(`${API_URL}/api/bugs/${bug._id}/priority`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ priority: next }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to update priority');
      }
    } catch (err) {
      console.error('Failed to update priority', err);
    } finally {
      setPrioritySaving((prev) => {
        const { [bug._id]: _, ...rest } = prev;
        return rest;
      });
      fetchBugs();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!desc.trim()) return;
    setReporting(true);
    try {
      const res = await fetch(`${API_URL}/api/bugs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          description: desc.trim(),
          details,
          login: localStorage.getItem('login'),
          displayName: localStorage.getItem('login'),
        }),
      });
      if (!res.ok) {
        alert('Failed to create bug');
        return;
      }
      const bug = await res.json();
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const upRes = await fetch(`${API_URL}/api/bugs/${bug._id}/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const upData = await upRes.json().catch(() => ({}));
        if (!upRes.ok) {
          alert(getMediaErrorMessage(upData.error, t));
        }
      }
      setDesc('');
      setDetails('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchBugs();
    } finally {
      setReporting(false);
    }
  };

  const closeBug = async (id) => {
    setClosingId(id);
    try {
      const res = await fetch(`${API_URL}/api/bugs/${id}/close`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        fetchBugs();
        setPinned((prev) => prev.filter((p) => p !== id));
      } else {
        alert('Failed to close bug');
      }
    } finally {
      setClosingId(null);
    }
  };

  const togglePin = (id) => {
    setPinned((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const saveEdit = async (id) => {
    setSavingEditId(id);
    try {
      const res = await fetch(`${API_URL}/api/bugs/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ description: editDesc, details: editDetails }),
      });
      if (res.ok) {
        if (editFile) {
          const fd = new FormData();
          fd.append('file', editFile);
          const upRes = await fetch(`${API_URL}/api/bugs/${id}/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          const upData = await upRes.json().catch(() => ({}));
          if (!upRes.ok) {
            alert(getMediaErrorMessage(upData.error, t));
          }
        }
        setEditing(null);
        setEditDesc('');
        setEditDetails('');
        setEditFile(null);
        if (editFileInputRef.current) editFileInputRef.current.value = '';
        fetchBugs();
      } else {
        alert('Failed to update bug');
      }
    } finally {
      setSavingEditId(null);
    }
  };

  const updateStatus = async (id, status) => {
    setStatusSavingId(id);
    try {
      const actor = {};
      if (userId) actor.id = userId;
      const actorLogin = localStorage.getItem('login');
      if (actorLogin) actor.login = actorLogin;
      const actorDisplayName = localStorage.getItem('displayName');
      if (actorDisplayName) actor.displayName = actorDisplayName;
      else if (actorLogin) actor.displayName = actorLogin;
      const payload = { status };
      if (Object.keys(actor).length) payload.actor = actor;
      const res = await fetch(`${API_URL}/api/bugs/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        fetchBugs();
      } else {
        alert('Failed to update status');
      }
    } finally {
      setStatusSavingId(null);
    }
  };

  const addComment = async (id) => {
    const text = (newComments[id] || '').trim();
    if (!text) return;
    await fetch(`${API_URL}/api/bugs/${id}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ text }),
    });
    setNewComments((prev) => ({ ...prev, [id]: '' }));
    fetchBugs();
  };

  const formatDate = (d) => {
    const date = new Date(d);
    const locale = lang === 'he' ? 'he-IL' : lang === 'es' ? 'es-ES' : lang;
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const exportCsv = () => {
    const escapeCsv = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      'ID',
      'Description',
      'Created At',
      'Author',
      'Status',
      'Last Updated',
      'Comments',
      'Is Pinned',
    ];
    const rows = filteredList.map((b) => [
      b._id,
      b.description,
      b.createdAt,
      b.createdBy?.login || b.createdBy?.displayName || '',
      b.status,
      b.updatedAt,
      Array.isArray(b.comments) ? b.comments.length : 0,
      pinned.includes(b._id) ? 'true' : 'false',
    ]);
    const lines = [header, ...rows].map((r) => r.map(escapeCsv).join(','));
    const csv = '\uFEFF' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const name = `bugs_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}.csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const normalizeStatus = (status) => {
    const s = (status ?? '').toString().trim().toLowerCase();
    return s || 'unknown';
  };

  const statusLabel = (status) => {
    const normalized = normalizeStatus(status);
    switch (normalized) {
      case 'very_urgent':
        return t('veryUrgent');
      case 'urgent':
        return t('urgent');
      case 'open':
        return t('open');
      case 'in_progress':
        return t('inProgress');
      case 'testing':
        return t('testing');
      case 'fixed':
        return t('fixed');
      case 'closed':
        return t('closed');
      default:
        return normalized || '—';
    }
  };

  const statusBadge = (b) => {
    const status = normalizeStatus(b.status);
    const cls =
      status === 'very_urgent'
        ? 'bg-danger text-white fw-bold'
        : status === 'urgent'
        ? 'bg-warning text-dark'
        : status === 'open'
        ? 'bg-danger'
        : status === 'in_progress'
        ? 'bg-secondary'
        : status === 'testing'
        ? 'bg-info text-dark'
        : status === 'fixed'
        ? 'bg-primary text-white'
        : status === 'closed'
        ? 'bg-success'
        : 'bg-secondary';
    return (
      <span className={'badge me-1 ' + cls}>{statusLabel(status)}</span>
    );
  };

  const BugItem = (b) => {
    const cardCls = () => {
      const s = normalizeStatus(b.status);
      return s === 'very_urgent'
        ? 'border-danger bg-danger-subtle'
        : s === 'urgent'
        ? 'border-warning bg-warning-subtle'
        : s === 'open'
        ? 'border-danger bg-light'
        : s === 'in_progress'
        ? 'border-secondary bg-light'
        : s === 'testing'
        ? 'border-info bg-light'
        : s === 'fixed'
        ? 'border-primary bg-light'
        : s === 'closed'
        ? 'border-success bg-light'
        : 'border-secondary bg-light';
    };

    const statusHistoryList = Array.isArray(b.statusHistory)
      ? [...b.statusHistory].sort((a, c) => new Date(c?.changedAt || 0) - new Date(a?.changedAt || 0))
      : [];

    const shortDesc =
      b.description.length > 100 ? `${b.description.slice(0, 100)}…` : b.description;

    const toggleExpand = () => setExpandedId((p) => (p === b._id ? null : b._id));
    const stop = (e) => e.stopPropagation();

    const commentsDesc = Array.isArray(b.comments)
      ? [...b.comments].sort(
          (a, c) => new Date(c.createdAt) - new Date(a.createdAt)
        )
      : [];
    const lastComment = commentsDesc[0];
    const show = showComments[b._id];
    const toggleShowAll = () =>
      setShowComments((prev) => ({ ...prev, [b._id]: !prev[b._id] }));

    const isPinned = pinned.includes(b._id);

    const isNew = Date.now() - new Date(b.createdAt).getTime() < 12 * 60 * 60 * 1000;
    const isRecent = highlightIds[b._id];
    const priorityValue = Number.isFinite(b.priority) ? b.priority : 1;
    const canAdjustPriority = canManagePriority(b);

    const priorityControls = (
      <div className="d-flex align-items-center gap-2 mt-2" onClick={stop}>
        <span className="badge bg-info text-dark">
          {t('priorityLabel', priorityValue)}
        </span>
        {canAdjustPriority && (
          <div className="btn-group btn-group-sm" role="group" aria-label="Priority controls">
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={prioritySaving[b._id] || priorityValue >= 10}
              title={t('priorityHigher')}
              aria-label={t('priorityHigher')}
              onClick={(e) => {
                e.stopPropagation();
                changePriority(b, 1);
              }}
            >
              ▲
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={prioritySaving[b._id] || priorityValue <= 1}
              title={t('priorityLower')}
              aria-label={t('priorityLower')}
              onClick={(e) => {
                e.stopPropagation();
                changePriority(b, -1);
              }}
            >
              ▼
            </button>
          </div>
        )}
      </div>
    );

    const collapsed = (
      <div onClick={toggleExpand}>
        <div className="d-flex justify-content-between align-items-start">
          <h5 className="card-title mb-1">{shortDesc}</h5>
          <div>
            {isNew && <span className="ms-2" title={t('recentlyCreated')}>🆕</span>}
            {isRecent && <span className="ms-2" title={t('recentUpdate')}>⚡</span>}
            {isPinned && <span className="ms-2">📌</span>}
          </div>
        </div>
        <small className="text-muted d-block">
          {formatDate(b.createdAt)} {statusBadge(b)}
        </small>
        <small className="text-muted d-block">
          {t('openedBy')}: {b.createdBy?.login || '-'}
        </small>
        {lastComment && (
          <small className="text-muted d-block">
            💬 {lastComment.author || 'Unknown'}: {lastComment.text.slice(0, 50)}
          </small>
        )}
        {priorityControls}
        <div className="d-flex justify-content-center mt-3">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            aria-label="Expand bug details"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand();
            }}
          >
            {t('viewMore')} <span aria-hidden="true">▼</span>
          </button>
        </div>
      </div>
    );

    const expanded = (
      <>
        <div className="d-flex flex-column flex-md-row" onClick={stop}>
          <div className="bug-content text-break pe-md-3 mb-2 mb-md-0">
            {editing === b._id ? (
              <>
                <input
                  className="form-control w-100 mb-3"
                  value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
              />
              <textarea
                className="form-control w-100 mb-3"
                rows="3"
                value={editDetails}
                onChange={(e) => setEditDetails(e.target.value)}
              />
              {(() => {
                const attachments = Array.isArray(b.photos) && b.photos.length
                  ? normalizeMediaList(b.photos)
                  : b.screenshotUrl
                  ? normalizeMediaList([b.screenshotUrl])
                  : [];
                if (!attachments.length) return null;
                return (
                  <div className="mb-2">
                    {attachments.map((media, idx) => (
                      <span key={media.publicId || media.url || idx} className="mb-1 me-1 d-inline-block">
                        {media.type === 'video' ? (
                          <video
                            src={media.url}
                            poster={media.thumbnailUrl || media.url}
                            controls
                            style={{ maxWidth: '120px' }}
                            className="cursor-pointer"
                            onClick={() => setViewImg(media)}
                          />
                        ) : (
                          <img
                            src={media.url}
                            alt="screenshot"
                            loading="lazy"
                            style={{ maxWidth: '100px' }}
                            className="mb-1 me-1 cursor-pointer"
                            onClick={() => setViewImg(media)}
                          />
                        )}
                      </span>
                    ))}
                  </div>
                );
              })()}
              <ImageUploadInput
                className="form-control w-100 mb-3"
                onChange={handleEditFileInput}
                ref={editFileInputRef}
              />
              <button
                className="btn btn-sm btn-primary me-2"
                disabled={savingEditId === b._id}
                onClick={() => saveEdit(b._id)}
              >
                {savingEditId === b._id ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  t('save')
                )}
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setEditing(null);
                  setEditFile(null);
                  if (editFileInputRef.current)
                    editFileInputRef.current.value = '';
                }}
              >
                {t('cancel')}
              </button>
            </>
          ) : (
            <>
              <p className="mb-1">
                {b.description}
                {b.details && <small className="d-block text-muted">{b.details}</small>}
              </p>
              {(() => {
                const attachments = Array.isArray(b.photos) && b.photos.length
                  ? normalizeMediaList(b.photos)
                  : b.screenshotUrl
                  ? normalizeMediaList([b.screenshotUrl])
                  : [];
                if (!attachments.length) return null;
                return (
                  <div className="mb-2">
                    {attachments.map((media, idx) => (
                      <span key={media.publicId || media.url || idx} className="mb-1 me-1 d-inline-block">
                        {media.type === 'video' ? (
                          <video
                            src={media.url}
                            poster={media.thumbnailUrl || media.url}
                            controls
                            style={{ maxWidth: '120px' }}
                            className="cursor-pointer"
                            onClick={() => setViewImg(media)}
                          />
                        ) : (
                          <img
                            src={media.url}
                            alt="screenshot"
                            loading="lazy"
                            style={{ maxWidth: '100px' }}
                            className="mb-1 me-1 cursor-pointer"
                            onClick={() => setViewImg(media)}
                          />
                        )}
                      </span>
                    ))}
                  </div>
                );
              })()}
              <small className="text-muted d-block">
                {formatDate(b.createdAt)} {statusBadge(b)}
              </small>
              <small className="text-muted d-block">
                {t('openedBy')}: {b.createdBy?.login || '-'}
              </small>
              {priorityControls}
              <div className="mt-3">
                <h6 className="fw-semibold">{t('statusHistory')}</h6>
                {statusHistoryList.length ? (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-3">
                      <thead>
                        <tr>
                          <th>{t('statusHistoryFrom')}</th>
                          <th>{t('statusHistoryTo')}</th>
                          <th>{t('statusHistoryBy')}</th>
                          <th>{t('statusHistoryAt')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statusHistoryList.map((entry, idx) => {
                          const key = `${entry?.changedAt || 'entry'}-${idx}`;
                          const fromLabel = entry?.from ? statusLabel(entry.from) : '—';
                          const toLabel = statusLabel(entry?.to);
                          const author =
                            entry?.author?.displayName ||
                            entry?.author?.login ||
                            entry?.author?.id ||
                            '—';
                          return (
                            <tr key={key}>
                              <td>{fromLabel}</td>
                              <td>{toLabel}</td>
                              <td>{author}</td>
                              <td>{entry?.changedAt ? formatDate(entry.changedAt) : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <small className="text-muted">{t('statusHistoryEmpty')}</small>
                )}
              </div>
              <BugActivity
                bug={b}
                showAll={show}
                toggleShowAll={toggleShowAll}
                formatDate={formatDate}
              />
              {token && (
                <>
                  <textarea
                    className="form-control form-control-lg w-100 mb-3"
                    rows="3"
                    placeholder={t('comment')}
                    value={newComments[b._id] || ''}
                    onChange={(e) =>
                      setNewComments({ ...newComments, [b._id]: e.target.value })
                    }
                  />
                  <button className="btn btn-primary" onClick={() => addComment(b._id)}>
                    Comment
                  </button>
                </>
              )}
              </>
            )}
          </div>
          <div className="bug-actions text-nowrap">
            {isNew && <span className="me-2" title={t('recentlyCreated')}>🆕</span>}
            {isRecent && <span className="me-2" title={t('recentUpdate')}>⚡</span>}
            {isPinned && <span className="me-2">📌</span>}
            <button
              className="btn btn-sm btn-outline-primary me-2"
            onClick={(e) => {
              e.stopPropagation();
              togglePin(b._id);
            }}
          >
            {isPinned ? t('unpin') : t('pin')}
          </button>
          {((b.createdBy?.id === userId && token) || ['admin', 'superuser'].includes(role)) && (
            <>
              {editing !== b._id && (
                <button
                  className="btn btn-sm btn-outline-secondary me-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditing(b._id);
                    setEditDesc(b.description);
                    setEditDetails(b.details || '');
                    setEditFile(null);
                    if (editFileInputRef.current) editFileInputRef.current.value = '';
                  }}
                >
                  {t('edit')}
                </button>
              )}
              {(b.status || '').toLowerCase() !== 'closed' && (
                <button
                  className="btn btn-sm btn-success me-2"
                  disabled={closingId === b._id}
                  onClick={(e) => {
                    e.stopPropagation();
                    closeBug(b._id);
                  }}
                >
                  {closingId === b._id ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    t('closeBug')
                  )}
                </button>
              )}
            </>
          )}
          {['admin', 'superuser'].includes(role) && (
            <div className="mt-2">
              <select
                className="form-select form-select-sm mb-1"
                value={normalizeStatus(b.status)}
                disabled={statusSavingId === b._id}
                onChange={(e) => {
                  e.stopPropagation();
                  updateStatus(b._id, e.target.value);
                }}
              >
                <option value="open">{t('open')}</option>
                <option value="in_progress">{t('inProgress')}</option>
                <option value="testing">{t('testing')}</option>
                <option value="fixed">{t('fixed')}</option>
                <option value="urgent">{t('urgent')}</option>
                <option value="very_urgent">{t('veryUrgent')}</option>
                <option value="closed">{t('closed')}</option>
              </select>
              <button
                className="btn btn-sm btn-primary mb-2"
                disabled={statusSavingId === b._id}
                onClick={(e) => {
                  e.stopPropagation();
                  updateStatus(b._id, normalizeStatus(b.status));
                }}
              >
                {statusSavingId === b._id ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  t('save')
                )}
              </button>
            </div>
          )}
          </div>
        </div>
        <div className="text-center mt-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand();
            }}
          >
            {t('collapse')} <span aria-hidden="true">▲</span>
          </button>
        </div>
      </>
    );

    return (
      <div
        key={b._id}
        className={`card mb-3 ${cardCls()} ${isRecent ? 'recent-highlight' : ''}`}
        onClick={toggleExpand}
      >
        <div
          className="card-body p-3"
          style={expandedId === b._id ? { maxHeight: '80vh', overflowY: 'auto' } : {}}
        >
          {expandedId === b._id ? expanded : collapsed}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="container-fluid px-3 mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="m-0">{t('bugs')}</h1>
        {['admin', 'superuser'].includes(role) && (
          <button className="btn btn-outline-secondary" onClick={exportCsv}>
            📤 {t('export')}
          </button>
        )}
      </div>
      <form onSubmit={submit} className="mb-4">
        <input
          className="form-control form-control-lg w-100 mb-3"
          placeholder={t('description')}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          required
        />
        <textarea
          className="form-control form-control-lg w-100 mb-3"
          rows="4"
          placeholder={t('details')}
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />
        <ImageUploadInput
          accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
          className="form-control form-control-lg w-100 mb-3"
          onChange={handleFileInput}
          ref={fileInputRef}
        />
        <button className="btn btn-primary" type="submit" disabled={reporting}>
          {reporting ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            t('reportBug')
          )}
        </button>
      </form>
      <div className="mb-3">
        <div className="form-check form-check-inline">
          <input
            id="onlyMine"
            type="checkbox"
            className="form-check-input"
            checked={onlyMine}
            onChange={(e) => setOnlyMine(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="onlyMine">
            {t('showOnlyMine')}
          </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            id="showClosed"
            type="checkbox"
            className="form-check-input"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="showClosed">
            {t('showClosed')}
          </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            id="urgentOnly"
            type="checkbox"
            className="form-check-input"
            checked={onlyUrgent}
            onChange={(e) => setOnlyUrgent(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="urgentOnly">
            {t('urgentOnly')}
          </label>
        </div>
        <select
          className="form-select form-select-sm d-inline-block w-auto ms-2"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="priority">{t('sortByPriority')}</option>
          <option value="dateDesc">{t('sortByDateDesc')}</option>
          <option value="dateAsc">{t('sortByDateAsc')}</option>
          <option value="status">{t('sortByStatus')}</option>
        </select>
        <div className="mt-1">
          <small>{t('shownOfTotal', shown, total)}</small>
        </div>
      </div>
      {pinnedListView.length > 0 && (
        <div className="mb-4">
          <h4>{t('pinnedBugs', pinnedListView.length)}</h4>
          {pinnedListView.map(BugItem)}
        </div>
      )}
      <div>{regularListView.length ? regularListView.map(BugItem) : <p>{t('noBugs')}</p>}</div>
      </div>
      {viewImg && (
        <div className="modal d-block" tabIndex="-1" role="dialog" onClick={() => setViewImg(null)}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-body p-0" onClick={(e) => e.stopPropagation()}>
                {viewImg.type === 'video' ? (
                  <video
                    src={viewImg.url}
                    poster={viewImg.thumbnailUrl || viewImg.url}
                    controls
                    autoPlay
                    className="img-fluid"
                  />
                ) : (
                  <img src={viewImg.url} alt="screenshot" loading="lazy" className="img-fluid" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}





